<template>
  <div>
    <!-- DEPÓSITOS PENDIENTES DE ACREDITACIÓN: una tarjeta por boleta -->
    <div class="cheques-deposits-cards" v-if="deposits.length">
      <div v-for="deposit in deposits" :key="deposit.id" class="cheques-deposit-card">
        <div class="cheques-deposit-card__header">
          <span class="cheques-deposit-card__icon" :class="'cash-kind--' + cashKindOf(deposit.cash && deposit.cash.kind).tone"><AppIcon :name="cashKindOf(deposit.cash && deposit.cash.kind).icon" /></span>
          <div class="cheques-deposit-card__title">
            <b-link class="cheques-deposit-card__cash" v-b-tooltip.hover title="Ver movimientos de la cuenta" @click="goToCashDetail(deposit)">{{ deposit.cash ? deposit.cash.name : '' }}</b-link>
            <span>Depósito #{{ deposit.id }} · {{ moment(deposit.date).format('DD/MM/YYYY') }}</span>
          </div>
          <span class="cheques-deposit-card__waiting" :class="'cheques-deposit-card__waiting--' + waiting(deposit).tone" v-b-tooltip.hover.bottom title="Tiempo transcurrido desde el depósito">
            <AppIcon name="hourglass_top" /> {{ waiting(deposit).text }}
          </span>
        </div>

        <div class="cheques-deposit-card__observations" v-if="deposit.observations"><AppIcon name="chat" /> {{ deposit.observations }}</div>

        <div class="cheques-deposit-card__items">
          <div v-for="item in activeItems(deposit)" :key="item.id" class="cheques-deposit-card__item">
            <div class="cheques-deposit-card__cheque">
              <strong>{{ item.cheque ? item.cheque.bank : '' }} N.º {{ item.cheque ? item.cheque.number : item.finances_cheques_id }}</strong>
              <span v-if="item.cheque">{{ item.cheque.transmitter_name }} · vto. {{ moment(item.cheque.date_expiration).format('DD/MM/YYYY') }}</span>
            </div>
            <span class="cheques-deposit-card__amount">{{ formatCurrency(item.amount) }}</span>
            <b-button variant="link" size="sm" class="cheques-deposit-card__cancel" v-b-tooltip.hover.bottom title="Anular este cheque del depósito" v-if="!item.accounting_lock" @click="cancelItem(item, deposit)"><AppIcon name="undo" /></b-button>
          </div>
        </div>

        <div class="cheques-deposit-card__footer">
          <span>{{ activeItems(deposit).length }} {{ activeItems(deposit).length == 1 ? 'cheque' : 'cheques' }}</span>
          <span class="cheques-deposit-card__total">Total <strong>{{ formatCurrency(deposit.amount_total) }}</strong></span>
          <b-button size="sm" variant="dark" @click="credit(deposit)"><AppIcon name="check_circle" class="mr-1" /> Acreditar</b-button>
        </div>
      </div>
    </div>
    <EmptyState v-else icon="hourglass_empty" title="No hay depósitos pendientes de acreditación" />

    <!-- ACREDITAR -->
    <b-modal v-model="modal.credit.active" size="lg" :key="'credit-' + modal.credit._id">
      <template #modal-header><div class="app-modal-header"><span class="app-modal-header__icon"><AppIcon name="check_circle" /></span><h5 class="app-modal-header__title">Acreditar depósito #{{ modal.credit.deposit ? modal.credit.deposit.id : '' }}</h5></div></template>

      <div v-if="modal.credit.deposit">
        <div class="cheques-deposits-summary">
          <div><span>Cuenta</span><strong>{{ modal.credit.deposit.cash ? modal.credit.deposit.cash.name : '' }}</strong></div>
          <div><span>Fecha depósito</span><strong>{{ moment(modal.credit.deposit.date).format('DD/MM/YYYY') }}</strong></div>
          <div><span>Cheques</span><strong>{{ activeItems(modal.credit.deposit).length }}</strong></div>
          <div><span>Importe bruto</span><strong>{{ formatCurrency(modal.credit.deposit.amount_total) }}</strong></div>
        </div>

        <b-row>
          <b-col md="4">
            <b-form-group label="Fecha de acreditación">
              <b-form-input type="date" size="sm" v-model="crud.credit.date_credited" :min="modal.credit.deposit.date" />
            </b-form-group>
          </b-col>
        </b-row>

        <div class="cheques-deposits-expenses-title">
          <span><AppIcon name="receipt_long" /> Gastos bancarios</span>
          <b-button variant="outline-dark" size="sm" @click="addExpense"><AppIcon name="add" class="mr-1" /> Agregar gasto</b-button>
        </div>

        <div v-if="!crud.credit.expenses.length" class="cheques-deposits-meta mb-2">Sin gastos. Si el banco descontó comisiones o impuestos, agregalos para que el saldo coincida con el extracto.</div>
        <div class="cheques-deposits-meta mb-2" v-if="allowPurchaseLink"><AppIcon name="info" /> Cada gasto es un movimiento de la cuenta, como en el extracto. Los tipos que llevan comprobante de compra quedan pendientes hasta cargarlo desde Movimientos de la cuenta.</div>

        <b-row v-for="(expense, index) in crud.credit.expenses" :key="index" class="cheques-deposits-expense-row">
          <b-col md="5">
            <b-form-select size="sm" v-model="expense.concept" :options="conceptOptions" />
          </b-col>
          <b-col md="3">
            <b-form-input size="sm" v-model="expense.description" placeholder="Detalle (opcional)" />
          </b-col>
          <b-col md="3">
            <b-input-group prepend="$" size="sm">
              <app-number-input v-model="expense.amount" />
            </b-input-group>
          </b-col>
          <b-col md="1" class="text-right">
            <b-button variant="link" size="sm" class="text-danger" @click="crud.credit.expenses.splice(index, 1)" v-b-tooltip.hover title="Quitar"><AppIcon name="delete" /></b-button>
          </b-col>
        </b-row>

        <div class="cheques-deposits-totals">
          <div><span>Bruto</span><strong>{{ formatCurrency(modal.credit.deposit.amount_total) }}</strong></div>
          <div><span>Gastos</span><strong class="text-danger">- {{ formatCurrency(creditExpensesTotal) }}</strong></div>
          <div class="cheques-deposits-totals__net"><span>Neto acreditado</span><strong>{{ formatCurrency(modal.credit.deposit.amount_total - creditExpensesTotal) }}</strong></div>
        </div>
      </div>

      <div slot="modal-footer">
        <b-button variant="outline-secondary" class="mr-1" @click="modal.credit.active=false"><AppIcon name="x" class="mr-1" /> Cancelar</b-button>
        <b-button variant="dark" @click="saveCredit()"><AppIcon name="save" class="mr-1" /> Acreditar</b-button>
      </div>
    </b-modal>

    <!-- ANULAR CHEQUE DEL DEPÓSITO -->
    <b-modal v-model="modal.cancel.active" :key="'cancel-' + modal.cancel._id">
      <template #modal-header><div class="app-modal-header"><span class="app-modal-header__icon"><AppIcon name="undo" /></span><h5 class="app-modal-header__title">Anular cheque del depósito</h5></div></template>

      <div v-if="modal.cancel.item">
        <div class="cheques-deposits-summary">
          <div><span>Cheque</span><strong>{{ modal.cancel.chequeLabel }}</strong></div>
          <div><span>Importe</span><strong>{{ formatCurrency(modal.cancel.item.amount) }}</strong></div>
          <div><span>Depósito</span><strong>#{{ modal.cancel.item.finances_cheques_deposits_id }}</strong></div>
        </div>

        <b-form-group label="Motivo">
          <b-form-radio-group v-model="crud.cancel.reason" stacked>
            <b-form-radio value="ERROR">Error de carga <small class="text-muted">— el cheque vuelve a cartera</small></b-form-radio>
            <b-form-radio value="OTHER">Otro motivo <small class="text-muted">— el cheque vuelve a cartera</small></b-form-radio>
          </b-form-radio-group>
        </b-form-group>

        <b-form-group :label="crud.cancel.reason == 'OTHER' ? 'Detalle del motivo (obligatorio)' : 'Observaciones'">
          <app-textarea v-model="crud.cancel.observations" placeholder="Ingresar el detalle..." rows="3" max-rows="6"></app-textarea>
        </b-form-group>
      </div>

      <div slot="modal-footer">
        <b-button variant="outline-secondary" class="mr-1" @click="modal.cancel.active=false"><AppIcon name="x" class="mr-1" /> Cancelar</b-button>
        <b-button variant="danger" @click="saveCancel()"><AppIcon name="undo" class="mr-1" /> Anular</b-button>
      </div>
    </b-modal>
  </div>
</template>
<script>
  import serviceAPI from '../services'
  import Error from '@/handler/error'
  import moment from 'moment'
  import { cashKindOf } from '../../config/cashKinds'

  export default {
    data: () => {
      return {
        deposits: [],
        crud: {
          credit: {
            date_credited: '',
            expenses: [],
          },
          cancel: {
            reason: 'ERROR',
            observations: '',
          },
        },
        modal: {
          credit: {
            active: false,
            deposit: null,
          },
          cancel: {
            active: false,
            item: null,
            chequeLabel: '',
          },
        },
        arr: {
          concepts: [],
        },
      }
    },
    computed: {
      allowPurchaseLink() {
        var deposit = this.modal.credit.deposit
        return !!(deposit && deposit.cash && deposit.cash.allow_purchase_link)
      },
      conceptOptions() {
        return this.arr.concepts.map(type => ({
          value: type.value,
          text: type.text + (type.purchase && this.allowPurchaseLink ? ' (lleva comprobante)' : ''),
        }))
      },
      pendingTotal() {
        return this.deposits.reduce((total, deposit) => total + parseFloat(deposit.amount_total), 0)
      },
      creditExpensesTotal() {
        return this.crud.credit.expenses.reduce((total, expense) => total + (parseFloat(expense.amount) || 0), 0)
      },
    },
    mounted() {
      this.load()
      this.loadConcepts()
    },
    methods: {
      cashKindOf,
      // tipos de movimiento bancario que son egresos (FinancesCashDetail::BANK_TYPES)
      loadConcepts() {
        serviceAPI.obtenerTiposMovimientoBancario().then((response) => {
          this.arr.concepts = response.data.filter(type => type.balance != '+')
        })
      },
      // antigüedad del depósito: el clearing suele tardar 24/48 h hábiles
      waiting(deposit) {
        const days = moment().startOf('day').diff(moment(deposit.date).startOf('day'), 'days')
        const text = days <= 0 ? 'Depositado hoy' : 'Esperando hace ' + days + (days == 1 ? ' día' : ' días')
        return { text, tone: days > 5 ? 'danger' : (days > 2 ? 'warning' : 'info') }
      },
      formatCurrency(value) {
        return Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS'}).format(value)
      },
      activeItems(deposit) {
        // anulados y rechazados ya no forman parte del depósito
        return (deposit.items || []).filter(item => !['CANCELLED', 'REJECTED'].includes(item.status))
      },
      load() {
        serviceAPI.filtrarDepositos({ status: 'PENDING' })
        .then((response) => {
          this.deposits = response.data
          this.$emit('loaded', this.deposits)
        })
        .catch(error => {
          this.$awn.alert(Error.showError(error))
        })
      },
      changed() {
        this.load()
        this.$emit('changed')
      },

      goToCashDetail(deposit) {
        this.$router.push({
          name: 'FinancesStaffCashDetail',
          params: { finances_cash_id: deposit.finances_cash_id, date_start: deposit.date },
        }).catch(() => {})
      },
      credit(deposit) {
        this.crud.credit.date_credited = moment().format('YYYY-MM-DD')
        this.crud.credit.expenses = []

        this.modal.credit.deposit = deposit
        this.modal.credit._id = Math.random().toString(36).slice(2, 8)
        this.modal.credit.active = true
      },
      addExpense() {
        this.crud.credit.expenses.push({ concept: 'COMISION', description: '', amount: 0 })
      },
      saveCredit() {
        let loader = this.$loading.show()
        serviceAPI.acreditarDeposito(this.modal.credit.deposit.id, this.crud.credit)
        .then(() => {
          loader.hide()
          this.modal.credit.active = false
          this.changed()
          this.$awn.success("Depósito acreditado")
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },

      // item: ítem del depósito (o destino del cheque: { item_id, deposit_id })
      cancelItem(item, deposit, chequeLabel) {
        this.crud.cancel.reason = 'ERROR'
        this.crud.cancel.observations = ''

        this.modal.cancel.item = item
        this.modal.cancel.chequeLabel = chequeLabel || (item.cheque ? item.cheque.bank + ' N.º ' + item.cheque.number : '')
        this.modal.cancel._id = Math.random().toString(36).slice(2, 8)
        this.modal.cancel.active = true
      },
      saveCancel() {
        let loader = this.$loading.show()
        serviceAPI.anularChequeDeposito(this.modal.cancel.item.id, this.crud.cancel)
        .then(() => {
          loader.hide()
          this.modal.cancel.active = false
          this.changed()
          this.$awn.success("Cheque anulado del depósito, vuelve a cartera")
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },

      revert(depositId) {
        let loader = this.$loading.show()
        serviceAPI.mostrarDeposito(depositId)
        .then((response) => {
          loader.hide()
          const deposit = response.data
          const items = this.activeItems(deposit)
          const message = 'Se eliminarán los movimientos de acreditación' + (deposit.expenses && deposit.expenses.length ? ' y gastos' : '') +
                          ' del depósito #' + deposit.id + ' en ' + (deposit.cash ? deposit.cash.name : '') +
                          ' (' + this.formatCurrency(deposit.amount_total) + ') y ' +
                          (items.length == 1 ? 'el cheque vuelve' : 'los ' + items.length + ' cheques vuelven') + ' a cartera.'

          return this.$bvModal.msgBoxConfirm(message, {
            title: 'Revertir acreditación',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Revertir',
            cancelTitle: 'Cancelar',
            footerClass: 'p-2',
            headerClass: 'app-confirm-header app-confirm-header--danger',
            centered: true
          })
        })
        .then(value => {
          if(!value) return

          let loaderRevert = this.$loading.show()
          serviceAPI.revertirDeposito(depositId, {})
          .then(() => {
            loaderRevert.hide()
            this.changed()
            this.$awn.success("Acreditación revertida, los cheques vuelven a cartera")
          })
          .catch(error => {
            loaderRevert.hide()
            this.$awn.alert(Error.showError(error))
          })
        })
        .catch(error => {
          loader.hide()
          this.$awn.alert(Error.showError(error))
        })
      },
    }
  }
</script>
<style scoped>
  .cheques-deposits-cards { display: grid; gap: 12px; grid-template-columns: repeat(auto-fill, minmax(420px, 1fr)); }
  .cheques-deposit-card { background: var(--app-color-surface); border: 1px solid var(--app-color-border); border-left: 5px solid var(--app-color-warning); border-radius: 8px; display: flex; flex-direction: column; }
  .cheques-deposit-card__header { align-items: center; display: flex; gap: 10px; padding: 12px 14px 8px; }
  .cheques-deposit-card__icon { align-items: center; border: 1px solid; border-radius: 8px; display: inline-flex; flex: 0 0 36px; height: 36px; justify-content: center; }
  .cheques-deposit-card__title { min-width: 0; }
  .cheques-deposit-card__cash { color: var(--app-color-primary-active); display: block; font-weight: var(--app-font-semibold); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .cheques-deposit-card__cash:hover { color: var(--app-color-primary); }
  .cheques-deposit-card__title span { color: var(--app-color-text-secondary); font-size: var(--app-text-xs); }
  .cheques-deposit-card__waiting { align-items: center; border: 1px solid; border-radius: 999px; display: inline-flex; font-size: var(--app-text-xs); font-weight: var(--app-font-semibold); gap: 3px; margin-left: auto; padding: 2px 9px; white-space: nowrap; }
  .cheques-deposit-card__waiting .material-symbols-rounded { font-size: 14px; }
  .cheques-deposit-card__waiting--info { background: var(--app-color-info-soft); border-color: var(--app-color-info-border); color: var(--app-color-info); }
  .cheques-deposit-card__waiting--warning { background: var(--app-color-warning-soft); border-color: var(--app-color-warning-border); color: var(--app-color-warning-hover); }
  .cheques-deposit-card__waiting--danger { background: var(--app-color-danger-soft); border-color: var(--app-color-danger-border); color: var(--app-color-danger); }
  .cheques-deposit-card__observations { color: var(--app-color-text-secondary); font-size: var(--app-text-xs); padding: 0 14px 6px 60px; }
  .cheques-deposit-card__observations .material-symbols-rounded { font-size: 14px; vertical-align: -3px; }
  .cheques-deposit-card__items { border-top: 1px solid var(--app-color-border); flex: 1; }
  .cheques-deposit-card__item { align-items: center; border-bottom: 1px dashed var(--app-color-border); display: flex; gap: 8px; padding: 7px 14px; }
  .cheques-deposit-card__item:last-child { border-bottom: none; }
  .cheques-deposit-card__cheque { display: grid; min-width: 0; }
  .cheques-deposit-card__cheque strong { font-size: var(--app-text-sm); }
  .cheques-deposit-card__cheque span { color: var(--app-color-text-secondary); font-size: var(--app-text-xs); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
  .cheques-deposit-card__amount { font-weight: var(--app-font-semibold); margin-left: auto; white-space: nowrap; }
  .btn.cheques-deposit-card__cancel { color: var(--app-color-danger); line-height: 1; padding: 0 2px; }
  .cheques-deposit-card__footer { align-items: center; background: var(--app-color-surface-subtle); border-radius: 0 0 8px 0; border-top: 1px solid var(--app-color-border); display: flex; gap: 12px; padding: 8px 14px; }
  .cheques-deposit-card__footer > span:first-child { color: var(--app-color-text-secondary); font-size: var(--app-text-xs); }
  .cheques-deposit-card__total { margin-left: auto; }
  .cheques-deposit-card__total strong { font-size: 15px; margin-left: 4px; }
  /* color por tipo de caja/cuenta (config/cashKinds.js) */
  .cash-kind--success { background: var(--app-color-success-soft); border-color: var(--app-color-success-border); color: var(--app-color-success); }
  .cash-kind--primary { background: var(--app-color-primary-soft); border-color: var(--app-color-info-border); color: var(--app-color-primary-active); }
  .cash-kind--warning { background: var(--app-color-warning-soft); border-color: var(--app-color-warning-border); color: var(--app-color-warning-hover); }
  .cash-kind--info { background: var(--app-color-info-soft); border-color: var(--app-color-info-border); color: var(--app-color-info); }
  .cash-kind--neutral { background: var(--app-color-surface-subtle); border-color: var(--app-color-border-strong); color: var(--app-color-text-secondary); }
  @media (max-width: 575.98px) { .cheques-deposits-cards { grid-template-columns: 1fr; } }
  .cheques-deposits-meta { color: var(--app-color-text-secondary); font-size: var(--app-text-xs); }
  .cheques-deposits-summary { background: var(--app-color-surface-subtle); border: 1px solid var(--app-color-border); border-radius: 7px; display: flex; flex-wrap: wrap; gap: 16px; margin-bottom: 12px; padding: 10px 12px; }
  .cheques-deposits-summary span { color: var(--app-color-text-secondary); display: block; font-size: var(--app-text-xs); }
  .cheques-deposits-expenses-title { align-items: center; border-top: 1px solid var(--app-color-border); display: flex; justify-content: space-between; margin-bottom: 8px; padding-top: 10px; }
  .cheques-deposits-expenses-title > span { font-weight: var(--app-font-semibold); }
  .cheques-deposits-expense-row { margin-bottom: 6px; }
  .cheques-deposits-totals { border-top: 1px solid var(--app-color-border); margin-left: auto; margin-top: 10px; max-width: 320px; padding-top: 8px; }
  .cheques-deposits-totals > div { display: flex; justify-content: space-between; }
  .cheques-deposits-totals__net { border-top: 1px dashed var(--app-color-border-strong); font-size: 15px; margin-top: 4px; padding-top: 4px; }
</style>
